"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Downsampler = void 0;
var array2d_1 = require("./array2d");
var image_1 = require("./image");
function resolutionString(resolution) {
  return "".concat(resolution.x, "x").concat(resolution.y);
}
exports.Downsampler = {
  /** Create a new downsampler for a 2D number array */
  fromNumbers: function (data) {
    var result = {
      mode: 'number',
      nColumns: data.nColumns,
      nRows: data.nRows,
      downsampled: {}
    };
    set(result, {
      x: data.nColumns,
      y: data.nRows
    }, data);
    return result;
  },
  /** Create a new downsampler for an image */
  fromImage: function (data) {
    var result = {
      mode: 'image',
      nColumns: data.nColumns,
      nRows: data.nRows,
      downsampled: {}
    };
    set(result, {
      x: data.nColumns,
      y: data.nRows
    }, data);
    return result;
  },
  /** Return the original (full-size) data */
  getOriginal: function (downsampler) {
    return get(downsampler, {
      x: downsampler.nColumns,
      y: downsampler.nRows
    });
  },
  /** Get data downsampled approximately to `minResolution`.
   * The returned resolution will be at least `minResolution` but less then double that, in each dimension.
   * The returned data will be equal to the original data if `minResolution` is big enough. */
  getDownsampled: function (downsampler, minResolution) {
    var targetResolution = {
      x: downsamplingTarget(downsampler.nColumns, minResolution.x),
      y: downsamplingTarget(downsampler.nRows, minResolution.y)
    };
    return getOrCompute(downsampler, targetResolution);
  }
};
/** Return `m`, a power of 2 or equal to `nDatapoints`, such that:
 * `nPixels <= m < 2*nPixels`  or  `m === nDatapoints < nPixels` */
function downsamplingTarget(nDatapoints, nPixels) {
  var result = 1;
  while (result < nPixels && result < nDatapoints) {
    result = Math.min(2 * result, nDatapoints);
  }
  return result;
}
/** Get data downsampled to `resolution` (exactly) if already computed. Do not compute anything. */
function get(downsampler, resolution) {
  return downsampler.downsampled[resolutionString(resolution)];
}
/** Save data downsampled to `resolution`. */
function set(downsampler, resolution, value) {
  downsampler.downsampled[resolutionString(resolution)] = value;
}
/** Get data downsampled to `resolution` (exactly), with caching.  */
function getOrCompute(downsampler, resolution) {
  var cached = get(downsampler, resolution);
  if (cached) {
    return cached;
  } else {
    var srcResolution = downsamplingSource(resolution, {
      x: downsampler.nColumns,
      y: downsampler.nRows
    });
    if (!srcResolution || srcResolution.x > downsampler.nColumns || srcResolution.y > downsampler.nRows) throw new Error('AssertionError');
    var srcData = getOrCompute(downsampler, srcResolution);
    var result = downsample(downsampler.mode, srcData, resolution);
    set(downsampler, resolution, result);
    return result;
  }
}
/** Return resolution from which `wanted` resolution should be obtained by downsampling.
 * This will have either X length or Y length doubled relative to `wanted` (or same as in `original` if doubled would be more that original)
 * and the other length kept the same.
 * Return `undefined` if `wanted` is already equal to `original`. */
function downsamplingSource(wanted, original) {
  if (wanted.x > original.x || wanted.y > original.y) {
    throw new Error('ArgumentError: Cannot downsample to higher resolution than original');
  }
  if (wanted.x === original.x && wanted.y === original.y) {
    // We already have it
    return undefined;
  }
  if (wanted.x === original.x || wanted.y !== original.y && wanted.x > wanted.y) {
    /* from up */
    return {
      x: wanted.x,
      y: Math.min(2 * wanted.y, original.y)
    };
  } else {
    /* from left */
    return {
      x: Math.min(2 * wanted.x, original.x),
      y: wanted.y
    };
  }
}
/** Downsample image or 2D array of numbers to a new size. */
function downsample(mode, input, newSize) {
  if (mode === 'image') {
    return downsampleImage(input, newSize);
  } else {
    return downsampleNumbers(input, newSize);
  }
}
/** Downsample 2D array of numbers to a new size. */
function downsampleNumbers(input, newSize) {
  if (input.nColumns === 2 * newSize.x && input.nRows === newSize.y) {
    return downsampleNumbers_halveX(input);
  }
  if (input.nColumns === newSize.x && input.nRows === 2 * newSize.y) {
    return downsampleNumbers_halveY(input);
  }
  return downsampleNumbers_general(input, newSize);
}
/** Downsample 2D array of numbers to a new size - implementation for general sizes. */
function downsampleNumbers_general(input, newSize) {
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = newSize.x;
  var h1 = newSize.y;
  array2d_1.Array2D.validateLength(input);
  var x = resamplingCoefficients(w0, w1);
  var y = resamplingCoefficients(h0, h1);
  var out = new Float32Array(h1 * w1);
  for (var i = 0; i < y.from.length; i++) {
    // row index
    var y_from_offset = y.from[i] * w0;
    var y_to_offset = y.to[i] * w1;
    var y_weight = y.weight[i];
    for (var j = 0; j < x.from.length; j++) {
      // column index
      var inputValue = input.values[y_from_offset + x.from[j]];
      if (inputValue === undefined) throw new Error('NotImplementedError: undefined values in data');
      out[y_to_offset + x.to[j]] += inputValue * y_weight * x.weight[j];
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out,
    isNumeric: true
  };
  return result;
}
/** Downsample 2D array of numbers to a new size - simplified implementation for special cases when newX===oldX/2, newY===oldY. */
function downsampleNumbers_halveX(input) {
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = Math.floor(w0 / 2);
  var h1 = h0;
  array2d_1.Array2D.validateLength(input);
  var out = new Float32Array(w1 * h1);
  for (var i = 0; i < h1; i++) {
    // row index
    for (var j = 0; j < w1; j++) {
      // column index
      var old1 = input.values[i * w0 + 2 * j];
      var old2 = input.values[i * w0 + 2 * j + 1];
      if (old1 === undefined || old2 === undefined) throw new Error('NotImplementedError: undefined values in data');
      var val = 0.5 * (old1 + old2);
      out[i * w1 + j] = val;
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out,
    isNumeric: true
  };
  return result;
}
/** Downsample 2D array of numbers to a new size - simplified implementation for special cases when newX===oldX, newY===oldY/2. */
function downsampleNumbers_halveY(input) {
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = w0;
  var h1 = Math.floor(h0 / 2);
  array2d_1.Array2D.validateLength(input);
  var out = new Float32Array(w1 * h1);
  for (var i = 0; i < h1; i++) {
    // row index
    for (var j = 0; j < w1; j++) {
      // column index
      var old1 = input.values[2 * i * w0 + j];
      var old2 = input.values[(2 * i + 1) * w0 + j];
      if (old1 === undefined || old2 === undefined) throw new Error('NotImplementedError: undefined values in data');
      var val = 0.5 * (old1 + old2);
      out[i * w1 + j] = val;
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out,
    isNumeric: true
  };
  return result;
}
/** Downsample image to a new size. */
function downsampleImage(input, newSize) {
  if (input.nColumns === 2 * newSize.x && input.nRows === newSize.y) {
    return downsampleImage_halveX(input);
  }
  if (input.nColumns === newSize.x && input.nRows === 2 * newSize.y) {
    return downsampleImage_halveY(input);
  }
  return downsampleImage_general(input, newSize);
}
/** Downsample image to a new size - implementation for general sizes. */
function downsampleImage_general(input, newSize) {
  var N_CHANNELS = 4;
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = newSize.x;
  var h1 = newSize.y;
  image_1.Image.validateLength(input);
  var x = resamplingCoefficients(w0, w1);
  var y = resamplingCoefficients(h0, h1);
  var out = new Uint8ClampedArray(h1 * w1 * N_CHANNELS); // We will always downsample by a factor between 1 and 2, so rounding errors here are not a big issue (for higher factors the errors could sum up to a big error)
  for (var i = 0; i < y.from.length; i++) {
    // row index
    for (var j = 0; j < x.from.length; j++) {
      // column index
      var fromOffset = (y.from[i] * w0 + x.from[j]) * N_CHANNELS;
      var toOffset = (y.to[i] * w1 + x.to[j]) * N_CHANNELS;
      var weight = y.weight[i] * x.weight[j];
      var a = input.values[fromOffset];
      var ra = input.values[fromOffset + 1];
      var ga = input.values[fromOffset + 2];
      var ba = input.values[fromOffset + 3];
      out[toOffset] += a * weight;
      out[toOffset + 1] += ra * weight;
      out[toOffset + 2] += ga * weight;
      out[toOffset + 3] += ba * weight;
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out
  };
  return result;
}
/** Downsample image to a new size - simplified implementation for special cases when newX===oldX/2, newY===oldY. */
function downsampleImage_halveX(input) {
  var N_CHANNELS = 4;
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = Math.floor(w0 / 2);
  var h1 = h0;
  image_1.Image.validateLength(input);
  var out = new Uint8ClampedArray(h1 * w1 * N_CHANNELS);
  for (var i = 0; i < h1; i++) {
    // row index
    for (var j = 0; j < w1; j++) {
      // column index
      var fromOffset1 = (i * w0 + 2 * j) * N_CHANNELS;
      var fromOffset2 = (i * w0 + 2 * j + 1) * N_CHANNELS;
      var toOffset = (i * w1 + j) * N_CHANNELS;
      out[toOffset] = 0.5 * (input.values[fromOffset1] + input.values[fromOffset2]);
      out[toOffset + 1] = 0.5 * (input.values[fromOffset1 + 1] + input.values[fromOffset2 + 1]);
      out[toOffset + 2] = 0.5 * (input.values[fromOffset1 + 2] + input.values[fromOffset2 + 2]);
      out[toOffset + 3] = 0.5 * (input.values[fromOffset1 + 3] + input.values[fromOffset2 + 3]);
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out
  };
  return result;
}
/** Downsample image to a new size - simplified implementation for special cases when newX===oldX, newY===oldY/2. */
function downsampleImage_halveY(input) {
  var N_CHANNELS = 4;
  var w0 = input.nColumns;
  var h0 = input.nRows;
  var w1 = w0;
  var h1 = Math.floor(h0 / 2);
  image_1.Image.validateLength(input);
  var out = new Uint8ClampedArray(h1 * w1 * N_CHANNELS);
  for (var i = 0; i < h1; i++) {
    // row index
    for (var j = 0; j < w1; j++) {
      // column index
      var fromOffset1 = (2 * i * w0 + j) * N_CHANNELS;
      var fromOffset2 = ((2 * i + 1) * w0 + j) * N_CHANNELS;
      var toOffset = (i * w1 + j) * N_CHANNELS;
      out[toOffset] = 0.5 * (input.values[fromOffset1] + input.values[fromOffset2]);
      out[toOffset + 1] = 0.5 * (input.values[fromOffset1 + 1] + input.values[fromOffset2 + 1]);
      out[toOffset + 2] = 0.5 * (input.values[fromOffset1 + 2] + input.values[fromOffset2 + 2]);
      out[toOffset + 3] = 0.5 * (input.values[fromOffset1 + 3] + input.values[fromOffset2 + 3]);
    }
  }
  var result = {
    nColumns: w1,
    nRows: h1,
    values: out
  };
  return result;
}
/** Calculate the weights of how much each pixel in the old image contributes to pixels in the new image, for 1D images
 * (pixel `from[i]` contributes to pixel `to[i]` with weight `weight[i]`).
 * Typically one old pixel will contribute to more new pixels and vice versa.
 * Sum of weights contributed to each new pixel must be equal to 1.
 * To use for 2D images, calculate row-wise and column-wise weights and multiply them. */
function resamplingCoefficients(nOld, nNew) {
  var scale = nNew / nOld;
  var i = 0; // Current pixel in the old image
  var j = 0; // Current pixel in the new image
  var p = 0; // Current continuous position in the new image
  var from = [];
  var to = [];
  var weight = [];
  while (p < nNew) {
    var nextINotch = scale * (i + 1);
    var nextJNotch = j + 1;
    if (nextINotch <= nextJNotch) {
      from.push(i);
      to.push(j);
      weight.push(nextINotch - p);
      p = nextINotch;
      i += 1;
      if (nextINotch === nextJNotch) {
        j += 1;
      }
    } else {
      from.push(i);
      to.push(j);
      weight.push(nextJNotch - p);
      p = nextJNotch;
      j += 1;
    }
  }
  return {
    /** Index of a pixel in the old image */
    from: from,
    /** Index of a pixel in the new image */
    to: to,
    /** How much the `from` pixel's value contributes to the `to` pixel */
    weight: weight
  };
}